import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { useAppSelector } from "@/redux/store";
import { useRouteSlug } from "@/hooks";
import { selectLoggedInStatus } from "@/util/selectors";
import useIsSmallScreen from "@/hooks/useIsSmallScreen";

import FollowedBrandsService from "@/services/FollowedBrandsService";
import { statusCache } from "../brandFollowCache";
import {
  usePendingFollowAction,
  writePendingFollowAction
} from "@/hooks/usePendingFollowAction";
import checkmarkBlue from "@/images/checkmark_blue.svg";

import "./style.scss";

type Props = {
  brandId: number;
  brandName: string;
};

const BrandFollowCard: React.FC<Props> = ({ brandId, brandName }) => {
  const { t } = useTranslation("productDetails");
  const history = useHistory();
  const routeSlug = useRouteSlug();
  const isLoggedIn = useAppSelector(selectLoggedInStatus);
  const { isSmallScreen } = useIsSmallScreen();

  const [isFollowing, setIsFollowing] = useState<boolean>(
    statusCache.get(brandId) ?? false
  );
  const [isPending, setIsPending] = useState<boolean>(false);
  const [statusLoaded, setStatusLoaded] = useState<boolean>(
    statusCache.has(brandId)
  );
  usePendingFollowAction({
    brandId,
    isLoggedIn,
    statusLoaded,
    isFollowing,
    statusCache,
    setIsFollowing
  });

  useEffect(() => {
    if (!isLoggedIn) {
      statusCache.clear();
      setIsFollowing(false);
      setStatusLoaded(true);
      return;
    }
    if (!brandId) return;
    if (statusCache.has(brandId)) {
      setIsFollowing(statusCache.get(brandId) as boolean);
      setStatusLoaded(true);
      return;
    }
    setStatusLoaded(false);
    let cancelled = false;
    FollowedBrandsService.getFollowStatus(brandId)
      .then(status => {
        if (cancelled) return;
        statusCache.set(brandId, status);
        setIsFollowing(status);
        setStatusLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setStatusLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [brandId, isLoggedIn]);

  const onClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      if (isPending) return;
      if (!isLoggedIn) {
        writePendingFollowAction(brandId, brandName);
        const currentPath = window.location.pathname.replace(
          `/${routeSlug}/`,
          ""
        );
        isSmallScreen
          ? history.replace(
              `/${routeSlug}/authorization?redirect=${currentPath}`
            )
          : history.push(`/${routeSlug}/authorization?redirect=${currentPath}`);
        return;
      }

      const next = !isFollowing;
      setIsFollowing(next);
      setIsPending(true);
      try {
        if (next) {
          await FollowedBrandsService.followBrand(brandId, brandName);
        } else {
          await FollowedBrandsService.unfollowBrand(brandId);
        }
        statusCache.set(brandId, next);
      } catch {
        setIsFollowing(!next);
        statusCache.set(brandId, !next);
      } finally {
        setIsPending(false);
      }
    },
    [
      brandId,
      brandName,
      history,
      isFollowing,
      isLoggedIn,
      isPending,
      routeSlug,
      isSmallScreen
    ]
  );

  if (!brandId || !brandName) return null;

  return (
    <div className="brand_follow_card">
      <div className="brand_follow_card__left">
        <div className="brand_follow_card__info">
          <span className="brand_follow_card__name">{brandName}</span>
          <span className="brand_follow_card__subtitle">
            {t("followBrandSubtitle")}
          </span>
        </div>
      </div>

      <button
        type="button"
        className={`brand_follow_card__btn${
          isFollowing ? " is_following" : ""
        }`}
        onClick={onClick}
        disabled={isPending || !statusLoaded}
        aria-pressed={isFollowing}
        aria-label={`${
          isFollowing ? t("followingBrand") : t("followBrand")
        } ${brandName}`}
      >
        <img
          className="brand_follow_card__btn_check"
          src={checkmarkBlue}
          alt="checkmark"
          aria-hidden="true"
        />
        {isFollowing ? t("followingBrand") : t("followBrand")}
      </button>
    </div>
  );
};

export default memo(BrandFollowCard);
